import { BRONZE_600 } from "@shared/brand-colors";
import { Switch, Route, useLocation, Redirect } from "wouter";
import { queryClient, setupBackgroundRefresh, prefetchCriticalData } from "./lib/queryClient";
import { QueryClientProvider, HydrationBoundary } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Component, useEffect, useRef, lazy, Suspense } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/use-auth";
import { BookingProvider } from "@/hooks/use-booking-context";
import { BookingLightboxHost } from "@/components/booking/booking-lightbox";
import { ProtectedRoute } from "./lib/protected-route";
import { scrollToTop, shouldPreventScrollToTop } from "./lib/utils";
import { trackPageView } from "@/lib/meta-pixel";
import { LazyWrapper, useIntelligentPreload } from "@/lib/lazy-components";
import { useGlobalFormKeyboardHandling } from "@/hooks/use-form-viewport";
import { SkeletonCard } from "@/components/ui/luxury-skeleton";
import { PageLoader, BlogPageLoader } from "@/components/ui/page-loader";
import { AdminCommandPalette } from "@/components/admin/admin-command-palette";
import { AdminKeyboardShortcuts } from "@/components/admin/admin-keyboard-shortcuts";
import { AdminShellTheme } from "@/components/admin/admin-layout";

interface AppErrorBoundaryState { hasError: boolean; error?: Error }
class AppErrorBoundary extends Component<{ children: ReactNode }, AppErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[AppErrorBoundary] Unhandled render error:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", background: "#F7EDDB", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
          <div style={{ maxWidth: "480px", textAlign: "center", fontFamily: "Bodoni Moda, Georgia, serif" }}>
            <div style={{ width: "64px", height: "64px", background: "rgba(168,146,112,0.12)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={BRONZE_600} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <h1 style={{ fontSize: "1.5rem", fontWeight: 400, color: "#1a1a1a", marginBottom: "12px" }}>Something went wrong</h1>
            <p style={{ fontSize: "0.95rem", color: "#6b7280", lineHeight: "1.6", marginBottom: "28px", fontFamily: "Montserrat, sans-serif" }}>
              An unexpected error occurred. Please reload the page or return to the home page.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button onClick={() => window.location.reload()} style={{ padding: "10px 24px", background: BRONZE_600, color: "#fff", border: "none", borderRadius: "0px", fontSize: "0.9rem", cursor: "pointer", fontFamily: "Montserrat, sans-serif" }}>
                Reload Page
              </button>
              <a href="/" style={{ padding: "10px 24px", background: "transparent", color: BRONZE_600, border: `1px solid ${BRONZE_600}`, borderRadius: "0px", fontSize: "0.9rem", textDecoration: "none", fontFamily: "Montserrat, sans-serif" }}>
                Go Home
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Critical pages loaded immediately (first paint optimization + SEO)
import HomePage from "@/pages/home-page";

// SSR public pages — eagerly imported so server and client render trees align exactly
import StayPageEager from "@/pages/stay-page";
import ContactPageEager from "@/pages/contact-page";
import PoliciesPageEager from "@/pages/policies-page";
import ReviewsPageEager from "@/pages/reviews-page";
import HouseRulesPageEager from "@/pages/house-rules-page";
import SitemapPageEager from "@/pages/sitemap-page";
import WaiverPageEager from "@/pages/waiver-page";
import ReleasePageEager from "@/pages/release-page";
import UseCasePageEager, { USE_CASES } from "@/pages/use-case-page";
import ExperiencesPage from "@/pages/experiences-page";
import BlogIndexPageEager from "@/pages/blog/blog-index-page";
import BlogCategoryPageEager from "@/pages/blog/blog-category-page";
import BlogPostPageEager from "@/pages/blog/blog-post-page";
import BookingPageEager from "@/pages/booking-page";
import { useFeatureFlags } from "@/hooks/use-feature-flags";

/** The old single-home /booking link goes to Our Homes once two homes are listed. */
function LegacyBookingRoute() {
  const flags = useFeatureFlags();
  return flags.publishedHomeCount > 1 ? <Redirect to="/properties" replace /> : <BookingPageEager />;
}
import FaqPageEager from "@/pages/faq-page";
import AboutPageEager from "@/pages/about-page";
import KaaawaPageEager from "@/pages/kaaawa-page";
import NearbyAttractionPageEager from "@/pages/nearby-attraction-page";
import SubmitReviewPageEager from "@/pages/submit-review-page";
import ReviewStatusPage from "@/pages/review-status-page";

// Lazy load main pages for better code splitting (booking is SSR-eager below)
const BookingDetailsPage = lazy(() => import("@/pages/booking-details-page"));
const AuthPage = lazy(() => import("@/pages/auth-page"));
const NotFound = lazy(() => import("@/pages/not-found"));

// Lazy load heavy components
import {
  CheckoutPage,
  BookingConfirmationPage,
  MyStayPage,
  BalancePaymentPage,
  ExtensionPaymentPage,
  AdminDashboard,
  AdminBookings,
  AdminCalendar,
  AdminContentManagement,
  AdminReporting,
  AdminSettings,
  AdminReviews,
  AdminCommunications,
  AdminBlog,
  AdminBacklinks,
  AdminLinkOpportunities,
  AdminOutreach,
  AdminUsers,
  AdminProperties,
  AdminLegal,
  AdminEmails,
} from "@/lib/lazy-components";
import PropertyPage, { PropertyDirectoryPage, StayRedirect } from "@/pages/property-page";

/**
 * Scroll to the element named by the URL hash.
 *
 * The router changes pages with pushState, which the browser does not treat
 * as a jump to an anchor, and the target often does not exist yet because the
 * page's data is still loading. So: retry for a few seconds until the element
 * is in the document, then scroll to it. Elements set scroll-margin-top for
 * the fixed header (a global rule in index.css).
 */
let activeHashScroll: number | null = null;

function scrollToHash(hash: string) {
  const id = decodeURIComponent(hash.replace(/^#/, ""));
  if (!id) return;
  if (activeHashScroll !== null) window.clearInterval(activeHashScroll);

  // One loop does everything: waits for the element to exist (its page data
  // may still be loading), scrolls to it, then keeps it aligned while the
  // sections above it finish loading and push it down. It looks the element
  // up on every tick, because a re-render can replace the node. It stops
  // when the visitor scrolls, or after eight seconds.
  let userScrolled = false;
  let scrolledOnce = false;
  const stop = () => { userScrolled = true; };
  const events: Array<keyof WindowEventMap> = ["wheel", "touchstart", "keydown"];
  events.forEach((e) => window.addEventListener(e, stop, { passive: true }));
  const startedAt = Date.now();
  const finish = () => {
    if (activeHashScroll !== null) window.clearInterval(activeHashScroll);
    activeHashScroll = null;
    events.forEach((e) => window.removeEventListener(e, stop));
  };
  activeHashScroll = window.setInterval(() => {
    if (userScrolled || Date.now() - startedAt > 8000) return finish();
    const el = document.getElementById(id);
    if (!el) return;
    const margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
    const drift = el.getBoundingClientRect().top - margin;
    if (Math.abs(drift) > 8) {
      el.scrollIntoView({ behavior: scrolledOnce ? "auto" : "smooth", block: "start" });
      scrolledOnce = true;
    }
  }, 150);
}

// Component to handle scroll-to-top behavior on route changes
function ScrollToTop() {
  const [location] = useLocation();
  const { preloadOnHover } = useIntelligentPreload();

  // Same-page anchor links (the path does not change, so the effect below
  // does not run) and back/forward between anchors.
  useEffect(() => {
    const onHash = () => { if (window.location.hash) scrollToHash(window.location.hash); };
    window.addEventListener("hashchange", onHash);
    window.addEventListener("pushState", onHash);
    window.addEventListener("replaceState", onHash);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("pushState", onHash);
      window.removeEventListener("replaceState", onHash);
    };
  }, []);
  
  useEffect(() => {
    // A link to another page's anchor: land on the anchor, not wherever the
    // previous page happened to be scrolled. Otherwise start at the top.
    if (shouldPreventScrollToTop(location)) {
      scrollToHash(window.location.hash);
    } else {
      scrollToTop('instant');
    }
    
    // Preload likely next routes based on current location
    if (location === '/') {
      preloadOnHover('booking');
      preloadOnHover('contact');
    } else if (location === '/booking') {
      preloadOnHover('checkout');
    }
  }, [location, preloadOnHover]);

  return null;
}

// Fires a Meta Pixel PageView on SPA route changes. The base snippet in index.html already
// fires the first PageView, so the initial render is skipped to avoid double counting.
function MetaPixelRouteTracker() {
  const [location] = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    trackPageView();
  }, [location]);

  return null;
}

// Performance setup component
function PerformanceSetup() {
  // App-wide safety net: keep any focused form field visible above the keyboard
  // / below the fixed header, on every form site-wide (guest and admin).
  useGlobalFormKeyboardHandling();

  useEffect(() => {
    setupBackgroundRefresh();

    if (!(window as typeof window & { __TANSTACK_DEHYDRATED_STATE__?: unknown }).__TANSTACK_DEHYDRATED_STATE__) {
      prefetchCriticalData();
    }
  }, []);

  return null;
}

// Enhanced loading fallback for main pages - shows logo while loading
function PageLoadingFallback() {
  return <PageLoader />;
}

function Router() {
  return (
    <>
      <ScrollToTop />
      <MetaPixelRouteTracker />
      <Switch>
        {/* Critical route - loaded immediately for LCP optimization */}
        <Route path="/" component={HomePage} />
        
        {/* Property stay details page — eagerly loaded for SSR hydration alignment */}
        <Route path="/stay" component={StayRedirect} />
        <Route path="/properties/:slug/book" component={BookingPageEager} />
        <Route path="/properties/:slug/reviews" component={ReviewsPageEager} />
        <Route path="/properties/:slug" component={PropertyPage} />
        <Route path="/properties" component={PropertyDirectoryPage} />
        
        {/* Main pages - SSR-public pages loaded eagerly for hydration tree alignment */}
        <Route path="/experiences" component={ExperiencesPage} />
        <Route path="/booking" component={LegacyBookingRoute} />
        <Route path="/faq" component={FaqPageEager} />
        <Route path="/about" component={AboutPageEager} />
        <Route path="/kaaawa" component={KaaawaPageEager} />
        <Route path="/nearby/:slug" component={NearbyAttractionPageEager} />
        <Route path="/booking/details" component={() => (
          <Suspense fallback={<PageLoadingFallback />}>
            <BookingDetailsPage />
          </Suspense>
        )} />
        
        <Route path="/submit-review" component={SubmitReviewPageEager} />
        <Route path="/review-expired" component={() => <ReviewStatusPage variant="expired" />} />
        <Route path="/review-not-found" component={() => <ReviewStatusPage variant="not-found" />} />
        <Route path="/review-error" component={() => <ReviewStatusPage variant="error" />} />

        {/* Secondary pages - lazy loaded */}
        <Route path="/booking/checkout" component={() => (
          <LazyWrapper><CheckoutPage /></LazyWrapper>
        )} />
        <Route path="/my-stay/:id" component={() => (
          <LazyWrapper><MyStayPage /></LazyWrapper>
        )} />
        <Route path="/booking/confirmation" component={() => (
          <LazyWrapper><BookingConfirmationPage /></LazyWrapper>
        )} />
        {/* Guest-facing balance payment for split-payment bookings (linked from email) */}
        <Route path="/booking/balance/:id" component={() => (
          <LazyWrapper><BalancePaymentPage /></LazyWrapper>
        )} />
        {/* Paying for extra nights added to an existing stay (linked from email and the stay page) */}
        <Route path="/booking/extension/:id" component={() => (
          <LazyWrapper><ExtensionPaymentPage /></LazyWrapper>
        )} />
        <Route path="/contact" component={ContactPageEager} />
        <Route path="/policies" component={PoliciesPageEager} />
        <Route path="/reviews" component={ReviewsPageEager} />
        <Route path="/house-rules" component={HouseRulesPageEager} />
        <Route path="/sitemap" component={SitemapPageEager} />
        {/* Per-adult waiver signing, reached from the pre-arrival email. */}
        <Route path="/waiver/:bookingId" component={WaiverPageEager} />
        <Route path="/waiver" component={WaiverPageEager} />
        {/* Guest Release, signed by every adult before the entry codes are released. */}
        <Route path="/release/:bookingId" component={ReleasePageEager} />
        <Route path="/families" component={() => <UseCasePageEager useCase={USE_CASES.families} />} />
        <Route path="/groups" component={() => <UseCasePageEager useCase={USE_CASES.groups} />} />
        
        {/* Blog routes - eagerly loaded so SSR and client render trees match */}
        <Route path="/blog" component={BlogIndexPageEager} />
        <Route path="/blog/category/:slug" component={BlogCategoryPageEager} />
        <Route path="/blog/:slug" component={BlogPostPageEager} />
        
        {/* Auth route - lazy loaded */}
        <Route path="/auth" component={() => (
          <Suspense fallback={<PageLoadingFallback />}>
            <AuthPage />
          </Suspense>
        )} />
        
        {/* Protected admin routes - lazy loaded */}
        <ProtectedRoute path="/admin" component={() => (
          <LazyWrapper fallback={<div className="p-8 space-y-6"><div className="h-8 bg-sand rounded animate-shimmer" /></div>}>
            <AdminDashboard />
          </LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/bookings" component={() => (
          <LazyWrapper><AdminBookings /></LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/calendar" component={() => (
          <LazyWrapper><AdminCalendar /></LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/communications" component={() => (
          <LazyWrapper><AdminCommunications /></LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/content-management" component={() => (
          <LazyWrapper><AdminContentManagement /></LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/blog" component={() => (
          <LazyWrapper><AdminBlog /></LazyWrapper>
        )} />
        <Route path="/admin/pricing">
          <Redirect to="/admin/settings?tab=pricing" />
        </Route>
        <ProtectedRoute path="/admin/reporting" component={() => (
          <LazyWrapper><AdminReporting /></LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/backlinks" component={() => (
          <LazyWrapper><AdminBacklinks /></LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/link-opportunities" component={() => (
          <LazyWrapper><AdminLinkOpportunities /></LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/outreach" component={() => (
          <LazyWrapper><AdminOutreach /></LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/reviews" component={() => (
          <LazyWrapper><AdminReviews /></LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/emails/:key" component={() => (
          <LazyWrapper><AdminEmails /></LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/emails" component={() => (
          <LazyWrapper><AdminEmails /></LazyWrapper>
        )} />
        <Route path="/admin/chat/:sessionId">
          <Redirect to="/admin/communications?tab=chat" />
        </Route>
        <Route path="/admin/chat">
          <Redirect to="/admin/communications?tab=chat" />
        </Route>
        <Route path="/admin/content">
          <Redirect to="/admin/content-management" />
        </Route>
        <ProtectedRoute path="/admin/settings" component={() => (
          <LazyWrapper><AdminSettings /></LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/users" component={() => (
          <LazyWrapper><AdminUsers /></LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/properties" component={() => (
          <LazyWrapper><AdminProperties /></LazyWrapper>
        )} />
        <ProtectedRoute path="/admin/legal" component={() => (
          <LazyWrapper><AdminLegal /></LazyWrapper>
        )} />
        
        {/* Fallback to 404 - lazy loaded */}
        <Route component={() => (
          <Suspense fallback={<PageLoadingFallback />}>
            <NotFound />
          </Suspense>
        )} />
      </Switch>
    </>
  );
}

declare global {
  interface Window {
    __TANSTACK_DEHYDRATED_STATE__?: unknown;
  }
}

function App() {
  const dehydratedState = typeof window !== "undefined"
    ? window.__TANSTACK_DEHYDRATED_STATE__
    : undefined;

  return (
    <AppErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={dehydratedState}>
            <AuthProvider>
              <BookingProvider>
                <TooltipProvider>
                  <PerformanceSetup />
                  <Toaster />
                  <AdminShellTheme />
                  <AdminCommandPalette />
                  <AdminKeyboardShortcuts />
                  {/* Chat widget hidden until the chat experience is fully functional.
                      Re-enable by restoring <LazyWrapper delay={100}><ChatWidget /></LazyWrapper> here. */}
                  <Router />
                  {/* Every booking link opens the calendar in a lightbox. */}
                  <BookingLightboxHost />
                </TooltipProvider>
              </BookingProvider>
            </AuthProvider>
          </HydrationBoundary>
        </QueryClientProvider>
      </HelmetProvider>
    </AppErrorBoundary>
  );
}

export default App;
